/**Import react section */
import React, { useState, useEffect, Fragment } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import propTypes from 'prop-types'
import { useHistory } from 'react-router-dom'

import { toSafeObject } from '../../store/helpers/StateHelper'
import { getUserManager } from '../../store/helpers/UserManager'
import { useIsMobile } from '../../hooks/useIsMobile'
import { hasAuthorization } from '../../store/session/sessionActions'

/**Import  Componets section*/
import ApplicationHeader from '../common/ApplicationHeader'
import Authorization from '../common/AuthorizationEvaluation'
import AvatarCustomSizes from '../common/AvatarCustomSizes'
import NotificationsPanel from '../common/NotificationsPanel'
import Toaster from '../common/Toaster'
/**Import resources section */
import {
	PORTAL_ADMINISTRATOR,
	SERVICE_PROVIDER_MANAGER,
	PROVIDER_REGISTRATION_VALIDATOR,
	SALES_REPRESENTATIVE_MANAGER,
	SALES_MANAGER,
	CUSTOMER_MANAGER,
	CUSTOMER_TECHNICIAN,
	DISPATCHER,
	VISUALIZER,
	PAYMENT_MANAGER
} from '../../resources/RolesEnum'
import { twoColumnsStyle as useStyles } from '../../resources/styles/LayoutStyles'
import { useAuthorizedToolbarStyles } from '../../resources/styles/ApplicationHeaderStyles'
import logoHeader from '../../resources/images/logoHeader.png'
import { NOTIFICATION_SERVICE } from '../../store/helpers/AppConstants'
import { executeLogout } from '../../store/session/sessionActions'

/** Material UI components */
import {
	IconButton,
	Avatar,
	Popper,
	ClickAwayListener,
	Typography,
	Paper,
	Divider,
	Button,
	DialogContent,
	Tooltip,
	List,
	ListItem,
	ListItemText,
	withStyles,
	Breadcrumbs,
	ListItemAvatar
} from '@material-ui/core/'
import SearchIcon from '@material-ui/icons/SearchTwoTone'
import MetricsIcon from '@material-ui/icons/BarChartOutlined'
import ServiceListIcon from '@material-ui/icons/Dashboard'
import SettingsIcon from '@material-ui/icons/Settings'
import ImberaUsersIcon from '@material-ui/icons/PeopleAlt'
import SalesPresentativeIcon from '@material-ui/icons/Business'
import ServiceProviderIcon from '@material-ui/icons/EmojiTransportation'
import ClientsIcon from '@material-ui/icons/LocalShipping'
import ProviderUsersIcon from '@material-ui/icons/PeopleAlt'
import AttachmentsFilesIcon from '@material-ui/icons/PictureAsPdf'
import TermsFilesIcon from '@material-ui/icons/FileCopy'
import ImageSearchIcon from '@material-ui/icons/ImageSearch'

import {
	loadNotificationsByUser,
	removeNotificationFromUserFromBack,
	downloadPendingNotifications,
	clearAllNotifications
} from '../../store/users/UsersActions'
import { DialogView } from '../common/Dialog'

/**
 * Authenticated layout
 */
export const Layout = (props) => {
	const classes = useStyles()

	const allChildElements = React.Children.toArray(props.children)
	const findChildElementByKey = (key) => allChildElements.find((x) => x.key && x.key.includes(key))

	const applicationToolbar = findChildElementByKey('AuthenticatedHeader')
	const sideBar = findChildElementByKey('AuthenticatedSideBar')
	const content = findChildElementByKey('AuthenticatedContent')

	return (
		<div className={classes.root}>
			<div className={classes.header}>{applicationToolbar}</div>
			<div className={classes.sideBar}>{sideBar}</div>
			<div className={classes.content}>{content}</div>
		</div>
	)
}

/**
 * Authenticated content
 */
export const Content = (props) => {
	return props.children
}

/**
 * Authenticated application header
 */
export const Header = (props) => {
	const userManager = getUserManager()
	let isActiveButton = false
	const isMobile = useIsMobile(props.isMobile)
	const loggedUser = useSelector((state) => toSafeObject(state.session.get('profile')))
	const notifications = useSelector((state) => toSafeObject(state.users.get('notifications')))
	const isDownloadingNotifications = useSelector((state) => state.users.get('isDownloadingNotifications'))
	const isClearingNotifications = useSelector((state) => state.users.get('isClearingNotifications'))
	const toolbarClasses = useAuthorizedToolbarStyles()
	const history = useHistory()
	const dispatch = useDispatch()
	const userProfile = props.user || loggedUser
	const [toasterOptions, setToasterOptions] = useState({ open: false, variant: null, message: null })
	/* The confirmation lives here and not in the panel: the popover closes on click-away, so a modal
	   the panel owned would be unmounted by the very click that opens it. */
	const [isClearConfirmationOpen, setIsClearConfirmationOpen] = useState(false)

	useEffect(() => {
		dispatch(loadNotificationsByUser())
	}, [dispatch])

	const openInternalUsersView = () => history.push('/internalusers')
	const openDashboarView = () => history.push('/dashboard')

	/**Redirect to sales representatives*/
	const openSalesRepresentativesClick = () => history.push('/salesRepresentatives')

	/**Redirect to service providers*/
	const openServiceProvidersView = () => history.push('/serviceProviders')

	/**Redirect to service customers*/
	const openCustomersView = () => history.push('/customers')

	/**Redirect to users from service provider logger*/
	const openProviderUsersView = () => {
		const accountProvider = userProfile.Account
		history.push(`/serviceProviders/${accountProvider.Code}/${accountProvider.Name}/${accountProvider.Token}`)
	}

	/**Redirect to files from service provider logger*/
	const openFilesProviderView = () => {
		history.push(`/serviceProviders/register/${userProfile.Account.Token}`)
	}

	const openTermsConditionsView = () => {
		history.push(`/termsConditions`)
	}
	const openWarrantySeriesSapView = () => {
		history.push(`/warrantySeriesSap`)
	}
	const openSubstituteMaterialsView = () => {
		history.push(`/substituteMaterials`)
	}
	const openImagesSapView = () => {
		history.push(`/SparePartImage`)
	}

	/**Redirect to users from sales rep provider logger*/
	const openSalesRepUsersView = () => {
		const accountSales = userProfile.Account
		history.push(`/salesRepresentatives/${accountSales.Code}/${accountSales.Name}/${accountSales.Token}`)
	}

	/**Redirect to users from cutomer user logger*/
	const openCustomerUsersView = () => {
		const accountCustomer = userProfile.Account
		history.push(`/customers/${accountCustomer.Code}/${accountCustomer.Name}/${accountCustomer.Token}`)
	}

	/** Redirect home */
	const redirectToHome = () => history.push(`/`)

	/**
	 * Execute logout
	 */
	const onLogout = () => {
		dispatch(executeLogout())
		userManager.signoutRedirect()
	}

	/**
	 * Redirect profile user logger
	 * @param {*} token
	 */
	const onEditProfile = (token) => {
		history.push(`/profile/${token}`)
	}

	/**
	 * Redirect payment rerquest pendient
	 * @param {*} requestToken
	 */
	const onNotificationRequestView = (notification) => {
		if (notification.RequestType === NOTIFICATION_SERVICE) {
			history.push(`/services/${notification.RequestToken}/-`)
			// Remove notification user
			dispatch(removeNotificationFromUserFromBack(notification.Token))
		} else if (notification.RequestType === 'PaymentRequest') {
			history.push(`/paymentrequests/${notification.RequestToken}`)
		} else if (notification.RequestType === 'EmailService') {
			history.push(`/services/${notification.RequestToken}/-`)
			dispatch(removeNotificationFromUserFromBack(notification.Token))
		} else {
			history.push(`/notifications/${notification.Token}`)
		}
	}

	/**
	 * Download the pending notifications of the general panel, surfacing a failure to the user
	 * so it is never silent.
	 */
	const onDownloadNotifications = () => {
		dispatch(downloadPendingNotifications()).catch(() =>
			setToasterOptions({ open: true, variant: 'error', message: 'An error was generated while downloading the notifications' })
		)
	}

	/**
	 * Opens the confirmation. Nothing is deleted until the user confirms.
	 */
	const onRequestClearAllNotifications = () => setIsClearConfirmationOpen(true)

	/**
	 * Closes the confirmation without deleting anything. Serves both the cancelling choice and the
	 * dialog's own close affordance, so dismissing the modal can never be read as a confirmation.
	 */
	const onCancelClearAllNotifications = () => setIsClearConfirmationOpen(false)

	/**
	 * Confirms the deletion. Guarded against a second click while the request is in flight: the
	 * deletion is physical and keeps no audit trail, so a duplicate submission is not recoverable.
	 * The modal closes only once the backend has confirmed; on failure it stays open with the
	 * toaster raised, so the user can retry without hunting for the action again.
	 */
	const onConfirmClearAllNotifications = () => {
		if (isClearingNotifications) return
		dispatch(clearAllNotifications())
			.then(() => setIsClearConfirmationOpen(false))
			.catch(() =>
				setToasterOptions({ open: true, variant: 'error', message: 'An error was generated while deleting the notifications' })
			)
	}

	return (
		<ApplicationHeader
			leftToolbar={
				<div className={toolbarClasses.leftToobarContainer}>
					{userProfile.Account.RequieredTermsAccepted ? (
						<img src={logoHeader} className={toolbarClasses.logoHeaderWithCursor} alt='manager-logo' />
					) : (
						<Tooltip title='Inicio' placement='right'>
							<img src={logoHeader} className={toolbarClasses.logoHeader} alt='manager-logo' onClick={redirectToHome} />
						</Tooltip>
					)}
					{isActiveButton && (
						<div>
							<Tooltip title='Search for'>
								<IconButton edge='start' size='medium' color='secondary'>
									<SearchIcon />
								</IconButton>
							</Tooltip>
						</div>
					)}
					<div>
						{isActiveButton && (
							<Tooltip title='Open service dashboard'>
								<IconButton edge='start' color='secondary' onClick={openDashboarView}>
									<ServiceListIcon />
								</IconButton>
							</Tooltip>
						)}
						{isActiveButton && (
							<Tooltip title='View service metrics'>
								<IconButton color='inherit'>
									<MetricsIcon />
								</IconButton>
							</Tooltip>
						)}
					</div>
					<div>{props.breadcrumbs}</div>
				</div>
			}
			rightToolbar={
				<div className={toolbarClasses.rightToobarContainer}>
					{!isMobile && (
						<NotificationsPanel
							notifications={notifications.filter((x) => x.RequestType === 'EmailService')}
							onNotificationRequestView={onNotificationRequestView}
							notificationType='email'
						/>
					)}
					{!isMobile && (
						<Authorization
							profile={userProfile}
							allowedRoles={[
								PORTAL_ADMINISTRATOR,
								PROVIDER_REGISTRATION_VALIDATOR,
								DISPATCHER,
								SALES_REPRESENTATIVE_MANAGER,
								SALES_MANAGER,
								CUSTOMER_MANAGER,
								CUSTOMER_TECHNICIAN
							]}
						>
							<NotificationsPanel
								notifications={notifications.filter((x) => x.RequestType !== 'PaymentRequest' && x.RequestType !== 'EmailService')}
								onNotificationRequestView={onNotificationRequestView}
								notificationType='general'
								onDownload={onDownloadNotifications}
								isDownloading={isDownloadingNotifications}
								onClearAll={onRequestClearAllNotifications}
								isClearing={isClearingNotifications}
							/>
						</Authorization>
					)}

					{!isMobile && (
						<Authorization profile={userProfile} allowedRoles={[PROVIDER_REGISTRATION_VALIDATOR]}>
							<NotificationsPanel
								notifications={notifications.filter((x) => x.RequestType === 'PaymentRequest')}
								onNotificationRequestView={onNotificationRequestView}
								notificationType='payment'
							/>
						</Authorization>
					)}
					{!userProfile.Account.RequieredTermsAccepted && !isMobile && notifications && notifications.length !== 0 && (
						<Authorization profile={userProfile} allowedRoles={[SERVICE_PROVIDER_MANAGER]}>
							<NotificationsPanel
								notifications={notifications}
								onNotificationRequestView={onNotificationRequestView}
								notificationType='general'
							/>
						</Authorization>
					)}

					{!isMobile && (
						<Authorization profile={userProfile} allowedRoles={[PORTAL_ADMINISTRATOR, PROVIDER_REGISTRATION_VALIDATOR, VISUALIZER, PAYMENT_MANAGER]}>
							<SettingsButton
								user={userProfile}
								onInternalUsersClick={openInternalUsersView}
								openSalesRepresentativesClick={openSalesRepresentativesClick}
								openServiceProvidersView={openServiceProvidersView}
								openCustomersView={openCustomersView}
								openTermsConditionsView={openTermsConditionsView}
								openWarrantySeriesSapView={openWarrantySeriesSapView}
								openSubstituteMaterialsView={openSubstituteMaterialsView}
								openImagesSapView={openImagesSapView}
							/>
						</Authorization>
					)}

					{!userProfile.Account.RequieredTermsAccepted && !isMobile && (
						<Authorization profile={userProfile} allowedRoles={[SERVICE_PROVIDER_MANAGER]}>
							<SettingsProviderButton
								user={userProfile}
								openProviderUsersViewClick={openProviderUsersView}
								openFilesProviderViewClick={openFilesProviderView}
								openTermsConditionsView={openTermsConditionsView}
							/>
						</Authorization>
					)}

					{!isMobile && (
						<Authorization profile={userProfile} allowedRoles={[SALES_REPRESENTATIVE_MANAGER]}>
							<SettingsSalesRepButton user={userProfile} openSalesRepUsersViewClick={openSalesRepUsersView} />
						</Authorization>
					)}

					{!isMobile && (
						<Authorization profile={userProfile} allowedRoles={[CUSTOMER_MANAGER]}>
							<SettingsCustomerButton user={userProfile} openCustomerUsersViewClick={openCustomerUsersView} />
						</Authorization>
					)}

					<UserAvatarButton user={userProfile} onCloseSession={onLogout} onEditProfile={onEditProfile} />

					{/** Toaster owned by the header — rendered unconditionally so the notice survives
					     the notifications popover closing, which would unmount a conditionally-rendered
					     Toaster before it finishes showing. */}
					<Toaster
						toasterOptions={toasterOptions}
						open={toasterOptions.open}
						onClose={() => setToasterOptions({ open: false, variant: null, message: null })}
					/>

					{/** The copy states the two things the user cannot recover from: that the deletion is
					     permanent, and that it reaches every notification and not only the ones the panel
					     in front of them is showing. The backend deletes the user's whole set. */}
					<DialogView
						open={isClearConfirmationOpen}
						isSaving={isClearingNotifications}
						onClose={onCancelClearAllNotifications}
						header={<strong>{'Delete all notifications'}</strong>}
						actions={
							<Fragment>
								<Button variant='outlined' color='primary' disabled={isClearingNotifications} onClick={onCancelClearAllNotifications}>
									CANCEL
								</Button>
								<Button variant='contained' color='secondary' disabled={isClearingNotifications} onClick={onConfirmClearAllNotifications}>
									DELETE
								</Button>
							</Fragment>
						}
					>
						<DialogContent>
							<Typography variant='body2'>
								This deletes <strong>every notification you have</strong> — including the payment and email notifications that
								are not listed in this panel.
							</Typography>
							<Typography variant='body2' style={{ marginTop: 8 }}>
								The notifications are removed permanently. They cannot be recovered afterwards, and no record of them is kept.
							</Typography>
						</DialogContent>
					</DialogView>
				</div>
			}
		/>
	)
}
Header.propTypes = {
	/**
	 * The logged user information to show in the user's profile
	 */
	user: propTypes.shape({
		name: propTypes.string.isRequired,
		email: propTypes.string.isRequired,
		roles: propTypes.array.isRequired
	}),

	/**
	 * The additional right toolbar options
	 */
	breadcrumbs: propTypes.element
}
Header.defaultProps = {
	breadcrumbs: null
}

/**
 * A styled breadcrumb for the authenticated header
 */
export const HeaderBreadCrum = withStyles((theme) => ({
	root: {
		marginTop: '12px'
	},
	separator: {
		color: theme.palette.common.white
	}
}))(Breadcrumbs)

const UserAvatarButton = (props) => {
	const [anchorEl, setAnchorEl] = useState(null)
	const classes = useAuthorizedToolbarStyles()
	const { user, onCloseSession, onEditProfile } = props
	return (
		<div>
			<IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
				<AvatarCustomSizes name={user.name} avatarUrl={user.avatarUrl} avatarStyle={classes.profileIconButton} isAvatarFromIdentity={true} />
			</IconButton>
			<Popper anchorEl={anchorEl} open={anchorEl !== null} className={classes.toolbarMenu}>
				<ClickAwayListener onClickAway={() => setAnchorEl(null)}>
					<Paper elevation={2}>
						<div className={classes.profilerForm}>
							<AvatarCustomSizes
								name={user.name}
								avatarUrl={user.avatarUrl}
								avatarStyle={classes.profileUserAvatar}
								isAvatarFromIdentity={true}
							/>
							<div className={classes.profilerUserInformation}>
								<Typography variant='subtitle2'>{user.name}</Typography>
								<Typography variant='caption'>{user.email}</Typography>
							</div>
						</div>
						<Divider />
						<div className={classes.profileFormButtons}>
							<Button size='small' color='primary' onClick={onCloseSession}>
								Close session
							</Button>
							<Button size='small' color='primary' onClick={() => onEditProfile(user ? user.Token : '')}>
								Edit profile
							</Button>
						</div>
					</Paper>
				</ClickAwayListener>
			</Popper>
		</div>
	)
}

const SettingsButton = (props) => {
	const classes = useAuthorizedToolbarStyles()
	const [anchorEl, setAnchorEl] = useState(null)
	return (
		<div>
			<IconButton edge='start' size='medium' color='secondary' onClick={(event) => setAnchorEl(event.currentTarget)}>
				<Avatar>
					<SettingsIcon color='secondary' />
				</Avatar>
			</IconButton>
			<Popper anchorEl={anchorEl} open={anchorEl !== null} className={classes.toolbarMenu}>
				<ClickAwayListener onClickAway={() => setAnchorEl(null)}>
					<Paper elevation={2}>
						<List dense>
							{hasAuthorization(props.user, [PORTAL_ADMINISTRATOR, VISUALIZER]) && (
								<>
									{hasAuthorization(props.user, [PORTAL_ADMINISTRATOR]) && (
										<ListItem button onClick={props.onInternalUsersClick}>
											<ListItemAvatar>
												<Avatar className={classes.avatarListIcon}>
													<ImberaUsersIcon className={classes.iconAvatar} />
												</Avatar>
											</ListItemAvatar>
											<ListItemText primary='Imbera users' />
										</ListItem>
									)}

									<ListItem button onClick={props.openSalesRepresentativesClick}>
										<ListItemAvatar>
											<Avatar className={classes.avatarListIcon}>
												<SalesPresentativeIcon className={classes.iconAvatar} />
											</Avatar>
										</ListItemAvatar>
										<ListItemText primary='Sales representatives' />
									</ListItem>
									<ListItem button onClick={props.openCustomersView}>
										<ListItemAvatar>
											<Avatar className={classes.avatarListIcon}>
												<ClientsIcon className={classes.iconAvatar} />
											</Avatar>
										</ListItemAvatar>
										<ListItemText primary='Customers' />
									</ListItem>
								</>
							)}
							{hasAuthorization(props.user, [PORTAL_ADMINISTRATOR, PAYMENT_MANAGER, PROVIDER_REGISTRATION_VALIDATOR]) && (
								<ListItem button onClick={props.openServiceProvidersView}>
									<ListItemAvatar>
										<Avatar className={classes.avatarListIcon}>
											<ServiceProviderIcon className={classes.iconAvatar} />
										</Avatar>
									</ListItemAvatar>
									<ListItemText primary='Service Providers' />
								</ListItem>
							)}
							<ListItem button onClick={props.openWarrantySeriesSapView}>
								<ListItemAvatar>
									<Avatar className={classes.avatarListIcon}>
										<TermsFilesIcon className={classes.iconAvatar} />
									</Avatar>
								</ListItemAvatar>
								<ListItemText primary='SAP Filters Warranty Series' />
							</ListItem>
							{hasAuthorization(props.user, [PORTAL_ADMINISTRATOR]) && (
								<ListItem button onClick={props.openSubstituteMaterialsView}>
									<ListItemAvatar>
										<Avatar className={classes.avatarListIcon}>
											<TermsFilesIcon className={classes.iconAvatar} />
										</Avatar>
									</ListItemAvatar>
									<ListItemText primary='Substitute Materials' />
								</ListItem>
							)}

							<ListItem button onClick={props.openTermsConditionsView}>
								<ListItemAvatar>
									<Avatar className={classes.avatarListIcon}>
										<TermsFilesIcon className={classes.iconAvatar} />
									</Avatar>
								</ListItemAvatar>
								<ListItemText primary='Terms and Conditions' />
							</ListItem>
							<ListItem button onClick={props.openImagesSapView}>
								<ListItemAvatar>
									<Avatar className={classes.avatarListIcon}>
										<ImageSearchIcon className={classes.iconAvatar} />
									</Avatar>
								</ListItemAvatar>
								<ListItemText primary='Serial Sap images' />
							</ListItem>
						</List>
					</Paper>
				</ClickAwayListener>
			</Popper>
		</div>
	)
}
// No defaults for the required callbacks: React resolves defaultProps before validating propTypes,
// so a default on a required prop means the isRequired warning never fires. That is how
// `openRetailsView` survived here as a required prop nobody ever passed — the default swallowed the
// warning that would have exposed it. The component consumes `openCustomersView`, which was the one
// missing from this contract.
SettingsButton.propTypes = {
	onInternalUsersClick: propTypes.func.isRequired,
	openCustomersView: propTypes.func.isRequired,
	openServiceProvidersView: propTypes.func.isRequired
}

/**
 * Setting options service provider manager
 * @param {*} props
 */
const SettingsProviderButton = (props) => {
	const classes = useAuthorizedToolbarStyles()
	const [anchorEl, setAnchorEl] = useState(null)
	return (
		<div>
			<IconButton edge='start' size='medium' color='secondary' onClick={(event) => setAnchorEl(event.currentTarget)}>
				<Avatar>
					<SettingsIcon color='secondary' />
				</Avatar>
			</IconButton>
			<Popper anchorEl={anchorEl} open={anchorEl !== null} className={classes.toolbarMenu}>
				<ClickAwayListener onClickAway={() => setAnchorEl(null)}>
					<Paper elevation={2}>
						<List dense>
							<ListItem button onClick={props.openProviderUsersViewClick}>
								<ListItemAvatar>
									<Avatar className={classes.avatarListIcon}>
										<ProviderUsersIcon className={classes.iconAvatar} />
									</Avatar>
								</ListItemAvatar>
								<ListItemText primary='Users' />
							</ListItem>
							<ListItem button onClick={props.openFilesProviderViewClick}>
								<ListItemAvatar>
									<Avatar className={classes.avatarListIcon}>
										<AttachmentsFilesIcon className={classes.iconAvatar} />
									</Avatar>
								</ListItemAvatar>
								<ListItemText primary='Attachments' />
							</ListItem>
							<ListItem button onClick={props.openTermsConditionsView}>
								<ListItemAvatar>
									<Avatar className={classes.avatarListIcon}>
										<TermsFilesIcon className={classes.iconAvatar} />
									</Avatar>
								</ListItemAvatar>
								<ListItemText primary='Terms and Conditions' />
							</ListItem>
						</List>
					</Paper>
				</ClickAwayListener>
			</Popper>
		</div>
	)
}
SettingsProviderButton.propTypes = {
	openProviderUsersViewClick: propTypes.func.isRequired,
	openFilesProviderViewClick: propTypes.func.isRequired
}
// No defaults for required callbacks — see the note on SettingsButton.propTypes.

/**
 * Setting options sales representatives manager
 * @param {*} props
 */
const SettingsSalesRepButton = (props) => {
	const classes = useAuthorizedToolbarStyles()
	const [anchorEl, setAnchorEl] = useState(null)
	return (
		<div>
			<IconButton edge='start' size='medium' color='secondary' onClick={(event) => setAnchorEl(event.currentTarget)}>
				<Avatar>
					<SettingsIcon color='secondary' />
				</Avatar>
			</IconButton>
			<Popper anchorEl={anchorEl} open={anchorEl !== null} className={classes.toolbarMenu}>
				<ClickAwayListener onClickAway={() => setAnchorEl(null)}>
					<Paper elevation={2}>
						<List dense>
							<ListItem button onClick={props.openSalesRepUsersViewClick}>
								<ListItemAvatar>
									<Avatar className={classes.avatarListIcon}>
										<ProviderUsersIcon className={classes.iconAvatar} />
									</Avatar>
								</ListItemAvatar>
								<ListItemText primary='Users' />
							</ListItem>
						</List>
					</Paper>
				</ClickAwayListener>
			</Popper>
		</div>
	)
}
SettingsSalesRepButton.propTypes = {
	openSalesRepUsersViewClick: propTypes.func.isRequired
}
// No defaults for required callbacks — see the note on SettingsButton.propTypes.

/**
 * Setting options customer manager
 * @param {*} props
 */
const SettingsCustomerButton = (props) => {
	const classes = useAuthorizedToolbarStyles()
	const [anchorEl, setAnchorEl] = useState(null)
	return (
		<div>
			<IconButton edge='start' size='medium' color='secondary' onClick={(event) => setAnchorEl(event.currentTarget)}>
				<Avatar>
					<SettingsIcon color='secondary' />
				</Avatar>
			</IconButton>
			<Popper anchorEl={anchorEl} open={anchorEl !== null} className={classes.toolbarMenu}>
				<ClickAwayListener onClickAway={() => setAnchorEl(null)}>
					<Paper elevation={2}>
						<List dense>
							<ListItem button onClick={props.openCustomerUsersViewClick}>
								<ListItemAvatar>
									<Avatar className={classes.avatarListIcon}>
										<ProviderUsersIcon className={classes.iconAvatar} />
									</Avatar>
								</ListItemAvatar>
								<ListItemText primary='Users' />
							</ListItem>
						</List>
					</Paper>
				</ClickAwayListener>
			</Popper>
		</div>
	)
}
SettingsCustomerButton.propTypes = {
	openCustomerUsersViewClick: propTypes.func.isRequired
}
// No defaults for required callbacks — see the note on SettingsButton.propTypes.
